package nl.appstuff.padelchecker.pages.padel

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import nl.appstuff.padelchecker.site.PageSeo
import nl.appstuff.padelchecker.site.StructuredData
import nl.appstuff.padelchecker.site.components.NavBar
import nl.appstuff.padelchecker.site.components.sections.FooterSection
import nl.appstuff.padelchecker.site.directory.Directory
import nl.appstuff.padelchecker.site.directory.DirectoryCity
import nl.appstuff.padelchecker.site.directory.DirectoryContentBox
import nl.appstuff.padelchecker.site.directory.DirectoryGrid
import nl.appstuff.padelchecker.site.directory.DirectoryHeading
import nl.appstuff.padelchecker.site.directory.DirectoryLink
import nl.appstuff.padelchecker.site.directory.DirectoryParagraph
import nl.appstuff.padelchecker.site.directory.DirectorySubheading
import nl.appstuff.padelchecker.site.localization.LocalSiteStrings
import nl.appstuff.padelchecker.site.localization.SiteStrings
import org.jetbrains.compose.web.css.vh

@Page("/padel-in/{province}")
@Composable
fun ProvincePage() {
    val strings = LocalSiteStrings.current
    val slug = rememberPageContext().route.params["province"].orEmpty()
    val province = Directory.province(slug)
    val cities = Directory.citiesInProvince(slug)
    val clubs = Directory.clubsInProvince(slug)

    if (province == null) {
        PageSeo(
            title = "${strings.directoryProvinceNotFoundTitle} – Padelchecker",
            description = strings.directoryProvinceNotFoundBody,
            canonicalPath = "/padel",
        )
    } else {
        PageSeo(
            title = strings.directoryProvinceSeoTitle(province.name, clubs.size),
            description = strings.directoryProvinceSeoDescription(clubs.size, province.name),
            canonicalPath = "/padel-in/${province.slug}",
        )
        ProvinceStructuredData(province.name, cities, strings)
    }

    Column(modifier = Modifier.fillMaxWidth().minHeight(100.vh)) {
        NavBar()
        DirectoryContentBox {
            if (province == null) {
                DirectoryHeading(strings.directoryProvinceNotFoundTitle)
                DirectoryParagraph(strings.directoryProvinceNotFoundBody)
                DirectoryLink("/padel", strings.directoryProvinceSeeAll)
            } else {
                DirectoryHeading(strings.directoryProvinceHeading(province.name))
                DirectoryParagraph(
                    strings.directoryProvinceIntro(clubs.size, province.name, cities.size, cities.size == 1)
                )

                DirectorySubheading(strings.directoryProvinceCitiesHeading(province.name))
                DirectoryGrid {
                    cities.sortedBy { it.name }.forEach { city ->
                        DirectoryLink(
                            href = "/padel/${city.slug}",
                            label = strings.directoryCityLink(city.name, city.clubCount),
                        )
                    }
                }

                DirectorySubheading(strings.directoryProvinceAllClubsHeading(province.name))
                DirectoryGrid {
                    clubs.sortedBy { it.name }.forEach { club ->
                        DirectoryLink(
                            href = "/padel/${club.citySlug}/${club.slug}",
                            label = strings.directoryProvinceClubInCity(club.name, club.cityName),
                        )
                    }
                }

                DirectoryLink("/padel", strings.directoryProvinceBackLink)
            }
        }
        FooterSection()
    }
}

@Composable
private fun ProvinceStructuredData(provinceName: String, cities: List<DirectoryCity>, strings: SiteStrings) {
    val items = cities.mapIndexed { i, city ->
        """
        {
          "@type": "ListItem",
          "position": ${i + 1},
          "url": "https://padelchecker.nl/site/padel/${city.slug}",
          "name": ${jsonString(strings.directoryStructuredDataPadelInCity(city.name))}
        }
        """.trimIndent()
    }.joinToString(",\n")
    StructuredData(
        """
        {
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": ${jsonString(strings.directoryStructuredDataClubsIn(provinceName))},
          "itemListElement": [
        $items
          ]
        }
        """.trimIndent()
    )
}
